import { cva, type VariantProps } from "class-variance-authority";

/**
 * Button style spec — token-driven, see docs/design-system/03-primitives.md.
 *
 * Heights/radii from primitives spec do not align 1:1 with default Tailwind
 * scale, so arbitrary values pin them precisely (26/30/36/42 px and 6/7/9/10 px).
 *
 * Hover darkens the bg ~8% via color-mix in OKLCH so light + dark themes share
 * one rule. Focus uses --shadow-focus-ring (4px ring at 18% --ring opacity).
 */
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 font-medium select-none whitespace-nowrap " +
    "transition-colors focus:outline-hidden focus-visible:shadow-[var(--shadow-focus-ring)] " +
    "disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--primary)] text-[var(--primary-foreground)] " +
          "hover:bg-[color-mix(in_oklch,var(--primary)_92%,black_8%)]",
        secondary:
          "bg-[var(--secondary)] text-[var(--foreground)] border border-[var(--border)] " +
          "hover:bg-[var(--accent)]",
        outline:
          "bg-[var(--card)] text-[var(--foreground)] border border-[var(--border)] " +
          "hover:bg-[var(--accent)]",
        ghost:
          "bg-transparent text-[var(--foreground)] hover:bg-[var(--accent)]",
        destructive:
          "bg-[var(--destructive)] text-[var(--destructive-foreground)] " +
          "hover:bg-[color-mix(in_oklch,var(--destructive)_92%,black_8%)]",
        // Alias kept for back-compat (5 callers as of phase 02). Migrate to `destructive`.
        danger:
          "bg-[var(--destructive)] text-[var(--destructive-foreground)] " +
          "hover:bg-[color-mix(in_oklch,var(--destructive)_92%,black_8%)]",
        link:
          "bg-transparent text-[var(--primary)] underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-[26px] px-2 text-xs rounded-[6px]",
        sm: "h-[30px] px-2.5 text-xs rounded-[7px]",
        md: "h-9 px-3.5 text-sm rounded-[9px]",
        lg: "h-[42px] px-[18px] text-sm rounded-[10px]",
        icon: "size-9 p-0 rounded-[9px]",
        "icon-sm": "size-[30px] p-0 rounded-[7px]",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    // `link` is inline text — reset size-derived dimensions.
    // Compound classes append after variant+size so tailwind-merge keeps these.
    compoundVariants: [
      {
        variant: "link",
        className: "h-auto p-0 rounded-none",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export const loadingSpinner = "animate-spin size-4";
